'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, ReceiptRows, type ReceiptRow } from '@kiao-voice/ui-kit'
import { TappayScript } from '../../payment/tappay/script'
import { couponReasonMessage } from '../coupon-reason-message'
import { readStoredToken } from '../../auth/auth-storage'
import { KiaoCardForm, type CardFormSubmitPayload } from '../../payment/tappay/kiao-card-form'
import { CouponField, type AppliedCoupon } from '../coupon-field'
import {
  EMPTY_INVOICE_FORM,
  InvoiceFields,
  NO_INVOICE_ERRORS,
  hasInvoiceError,
  invoiceFormFromSettings,
  invoiceSettingsFromForm,
  validateInvoiceForm,
  type InvoiceFormErrors,
  type InvoiceFormValue,
} from '../invoice-fields'
import type { CouponProductKind } from '../api-client'
import type {
  CheckoutErrorAction,
  CheckoutStrategy,
  CheckoutSuccessOutcome,
} from './checkout-types'
import type { PersonalPaidPlanCode, SubscriptionCycle } from '@kiao-voice/core'

type InvoiceLoadFn = typeof import('../api-client').fetchInvoiceSettings
type InvoiceSaveFn = typeof import('../api-client').saveInvoiceSettings

/** Coupon context for the panel's CouponField — what's being bought, so the
 *  server can resolve the base price for the discount preview. `null` hides the
 *  whole order-summary block (coupon + breakdown), e.g. for a bind-card flow. */
export interface CheckoutCouponContext {
  product: CouponProductKind
  planCode?: PersonalPaidPlanCode
  cycle?: SubscriptionCycle
  packCode?: string
  seats?: number
}

interface CheckoutPanelProps {
  /** Subtotal in TWD — the base the coupon → wallet → cash split is computed on. */
  amount: number
  /** Org wallet (儲值金) balance for the offset + card-free path. Null = no wallet
   *  (guest, flag off, or fetch failed) → full cash price, card required. */
  wallet?: { enabled: boolean; total: number } | null
  /** Coupon/order-summary context. Null → no coupon field + no breakdown (bind). */
  coupon: CheckoutCouponContext | null
  /** Pre-fills the cardholder email + the card-free button's cardholder. */
  userEmail: string | null
  /** The per-product behavior (which API, analytics, side effects). */
  strategy: CheckoutStrategy
  /** Trust/security note rendered above the order summary. */
  note: string
  /** Optional full-width purchase configuration rendered before the summary and
   *  payment columns (for Team name / cycle / seats). */
  setup?: React.ReactNode
  /** Optional replacement for the first breakdown row label. */
  subtotalLabel?: React.ReactNode
  /** Optional explanatory copy rendered directly below the breakdown. */
  summaryNote?: React.ReactNode
  /** Optional display formatter; arithmetic continues to use numeric TWD. */
  formatAmount?: (amount: number) => string | number
  /**
   * Whether a fully-covered (NT$0) checkout may skip the card form. Authed
   * subscribe/pack: true (the server mints an NT$0 internal trade). Guest: false
   * — a brand-new account has no wallet and the guest endpoint requires a prime,
   * so a 100%-off coupon still collects a card.
   */
  cardFree?: boolean
  /** Overrides the primary submit label (bind-card has no payable amount). */
  submitLabelOverride?: string
  /** Hint under the card form's email field (guest: email = account email). */
  emailHint?: string
  /** `data-testid` prefix for the breakdown rows + card-free button + error, so
   *  each surface keeps its existing test hooks (`pricing` / `billing`). */
  testIdPrefix: string
  /** `data-testid` for the form/card wrapper (varies per action on billing). */
  formTestId: string
  /** Changing this resets the panel's per-checkout state (coupon / forceCard /
   *  error) WITHOUT remounting — preserving the TapPay iframe lifetime. */
  resetKey: string
  /** Changing this clears quote-dependent state (coupon / forceCard / submit
   *  error) while preserving the buyer's in-progress invoice form. */
  quoteResetKey?: string
  /** Reports the in-flight state up so the dialog shell can disable its close. */
  onSubmittingChange?: (submitting: boolean) => void
  /** Called after a successful charge/bind — the container shows its own success
   *  feedback + refresh. `tradeId` is the real charge id when one happened. */
  onSuccess?: (tradeId?: number) => void
  /** Product-aware success metadata without breaking legacy trade-id callbacks. */
  onSuccessOutcome?: (outcome: CheckoutSuccessOutcome) => void
  /** Guest checkout hit an existing account → pivot to OTP sign-in. */
  onRequiresSignIn?: (email: string, paid: boolean) => void
  /** Hide the dialog (called on success / requiresSignIn before the pivot). */
  onClose: () => void
  /** The wallet stopped covering the price server-side → container may resync. */
  onCardRequired?: () => void
  /** 3DS navigation — test seam. */
  redirect?: (url: string) => void
  /** Skip the TapPay <Script> mount in tests. */
  skipScript?: boolean
  /** When BOTH are provided (authed purchases with an order summary), the summary
   *  column shows the 發票 toggle + fields, hydrated from `invoiceLoadFn` on open
   *  and saved via `invoiceSaveFn` just before the charge (the server reads the
   *  saved settings at async issuance time). Omit for guest / bind-card → no
   *  invoice section. */
  invoiceLoadFn?: InvoiceLoadFn
  invoiceSaveFn?: InvoiceSaveFn
}

/** Idempotency nonce for one checkout attempt. randomUUID where available,
 * else a timestamped random value. */
function makeCheckoutNonce(): string {
  const c = globalThis.crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  return `wco_${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`
}

/**
 * The shared checkout shell body (issue #249): order summary (折抵券 →
 * 儲值金 → 應付 breakdown) + card form (or a card-free CTA when cash = NT$0),
 * with EVERY server outcome mapped once. The product-specific bits — which API
 * to call, analytics, guest pivot, success copy — are injected via `strategy`
 * and the `on*` callbacks. Designed to drop into a `<dialog>` (see
 * `<CheckoutDialog>`) or render as a standalone page section.
 */
export function CheckoutPanel({
  amount,
  wallet = null,
  coupon,
  userEmail,
  strategy,
  note,
  setup,
  subtotalLabel,
  summaryNote,
  formatAmount = (value) => value,
  cardFree = true,
  submitLabelOverride,
  emailHint,
  testIdPrefix,
  formTestId,
  resetKey,
  quoteResetKey,
  onSubmittingChange,
  onSuccess,
  onSuccessOutcome,
  onRequiresSignIn,
  onClose,
  onCardRequired,
  redirect,
  skipScript,
  invoiceLoadFn,
  invoiceSaveFn,
}: CheckoutPanelProps) {
  const { t } = useTranslation('web')
  const [submitting, setSubmitting] = useState(false)
  const [recoveryPending, setRecoveryPending] = useState(false)
  // Declines/errors are shown INLINE (not via toast): the panel is rendered in a
  // <dialog> top layer, so a toast fires behind it (invisible). Inline
  // keeps the message with the card form so the buyer can fix + retry.
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitErrorAction, setSubmitErrorAction] = useState<CheckoutErrorAction | null>(null)
  // Set when a wallet-covered checkout is refused server-side (card_required) →
  // drop the wallet offset and collect a card for the post-coupon remainder.
  const [forceCard, setForceCard] = useState(false)
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null)
  // 發票 (invoice) — shown for any actual CHARGE with both seams present (authed
  // purchases AND wallet top-ups, which issue their invoice at top-up). Gated on
  // amount > 0 so bind-card (amount 0, no charge → no invoice) stays single-column.
  // Hydrated from the saved org settings on open; saved just before the charge so
  // the async issuance uses the right identity.
  const showInvoice = amount > 0 && Boolean(invoiceLoadFn && invoiceSaveFn)
  const [invoiceForm, setInvoiceForm] = useState<InvoiceFormValue>(EMPTY_INVOICE_FORM)
  const [invoiceErrors, setInvoiceErrors] = useState<InvoiceFormErrors>(NO_INVOICE_ERRORS)
  // Refs mirror the latest form + the in-flight load so runSubmit can DEFER the
  // charge until the saved identity has hydrated — reading the ref (not the
  // closed-over state) avoids charging with the stale personal default.
  const invoiceFormRef = useRef(invoiceForm)
  const invoiceLoadPromiseRef = useRef<Promise<unknown> | null>(null)
  // Set once the user edits the invoice fields, so a late-arriving load doesn't
  // clobber their in-progress 公司/統編 entry (which would revert the identity).
  const invoiceUserEditedRef = useRef(false)
  const applyInvoiceForm = useCallback((next: InvoiceFormValue) => {
    invoiceFormRef.current = next
    setInvoiceForm(next)
  }, [])

  // Idempotency nonce STABLE within one checkout attempt (so a double-submit,
  // response retry, or newly-tokenized prime still dedups server-side),
  // FRESH per attempt — `resetKey` changes on every modal open (see billing's
  // `openSeq`), so reopening the same target re-rolls it and a legitimate second
  // card-free purchase is NOT swallowed as a duplicate. A ref (not useMemo, which
  // React may evict mid-attempt) guarantees the identity holds for the attempt.
  const nonceRef = useRef<{ key: string; value: string }>(undefined)
  if (!nonceRef.current || nonceRef.current.key !== resetKey) {
    nonceRef.current = { key: resetKey, value: makeCheckoutNonce() }
  }
  const checkoutAttemptId = nonceRef.current.value

  // Reset per-checkout state when the target changes — WITHOUT remounting (a
  // coupon/error from one purchase must not bleed into the next, and the TapPay
  // card form must stay mounted so its hosted iframes survive).
  useEffect(() => {
    setAppliedCoupon(null)
    setForceCard(false)
    setSubmitError(null)
    setSubmitErrorAction(null)
    setRecoveryPending(false)
    setInvoiceErrors(NO_INVOICE_ERRORS)
    // Reset SYNCHRONOUSLY to the default so a fast re-open (different target /
    // context) can't save+charge with the previous checkout's invoice while the
    // async reload below is still in flight; the load effect repopulates it.
    applyInvoiceForm(EMPTY_INVOICE_FORM)
    invoiceUserEditedRef.current = false
  }, [resetKey, applyInvoiceForm])

  useEffect(() => {
    if (quoteResetKey === undefined) return
    setAppliedCoupon(null)
    setForceCard(false)
    setSubmitError(null)
    setSubmitErrorAction(null)
  }, [quoteResetKey])

  // (Re)load the saved invoice settings each time a checkout opens (resetKey
  // changes per open). Authed only — guest checkout omits the seams.
  useEffect(() => {
    if (!showInvoice || !invoiceLoadFn) {
      invoiceLoadPromiseRef.current = null
      return
    }
    let cancelled = false
    // Keep the promise so runSubmit can await it (defer the charge until the saved
    // identity hydrates) even if the user clicks pay mid-load.
    const loadPromise = invoiceLoadFn()
      .then((resp) => {
        // Don't clobber edits the user made while the load was in flight.
        if (!cancelled && !invoiceUserEditedRef.current) {
          applyInvoiceForm(invoiceFormFromSettings(resp.invoiceSettings))
        }
      })
      .catch((cause) => {
        reportHandledException(cause, {
          source: 'features/billing/checkout/checkout-panel.tsx',
        })
        // Non-critical: leave the default (personal) and let the charge proceed.
        // REPORT so a systematic outage isn't invisible (no silent catch).
        console.warn('checkout: failed to load invoice settings', cause)
      })
    invoiceLoadPromiseRef.current = loadPromise
    return () => {
      cancelled = true
    }
  }, [showInvoice, invoiceLoadFn, resetKey, applyInvoiceForm])

  useEffect(() => {
    onSubmittingChange?.(submitting)
  }, [submitting, onSubmittingChange])

  // Checkout split for DISPLAY — coupon → wallet → cash (the server is the
  // authoritative splitter). Once the server refuses with card_required we drop
  // the wallet offset (walletApplied=0): the card form falls back to the
  // post-coupon price — never a misleading "Pay NT$0" with a card still required.
  // The summary column shows for a coupon-bearing purchase OR whenever there's an
  // invoice to collect (e.g. a wallet top-up: 金額明細 + 發票 but NO coupon field).
  const hasSummary = coupon !== null || showInvoice
  const couponApplied = coupon !== null && appliedCoupon ? Math.min(appliedCoupon.twd, amount) : 0
  const afterCoupon = Math.max(0, amount - couponApplied)
  // Wallet offset applies only to a PURCHASE (coupon-bearing) — never a top-up
  // (you can't fund the wallet from itself), even though the summary now shows
  // for top-ups too (for the invoice).
  const walletAvailable = coupon !== null && wallet?.enabled ? wallet.total : 0
  const walletApplied = forceCard ? 0 : Math.min(walletAvailable, afterCoupon)
  const payable = Math.max(0, afterCoupon - walletApplied)
  const showBreakdown = couponApplied > 0 || walletApplied > 0
  const fullyCovered = showBreakdown && payable === 0
  const controlsDisabled = submitting || recoveryPending

  const navigate = useCallback(
    (url: string) => {
      if (redirect) redirect(url)
      else if (typeof window !== 'undefined') window.location.href = url
    },
    [redirect]
  )

  const runSubmit = useCallback(
    async (payload: CardFormSubmitPayload) => {
      if (recoveryPending) return
      setSubmitting(true)
      setSubmitError(null)
      setSubmitErrorAction(null)
      try {
        // Persist the invoice settings before charging — the server reads the
        // saved (org-scoped) identity at async issuance time. Block the charge on
        // invalid company fields; a SAVE failure is non-critical → report + charge.
        if (showInvoice && invoiceSaveFn) {
          // DEFER the charge until the saved identity has hydrated — otherwise a
          // user who clicks pay mid-load would save/issue the invoice with the
          // default PERSONAL identity (a company account billed as personal).
          // Read the REF, not the closed-over invoiceForm (stale after the await).
          if (invoiceLoadPromiseRef.current) {
            await invoiceLoadPromiseRef.current
          }
          const form = invoiceFormRef.current
          const nextInvoiceErrors = validateInvoiceForm(form)
          setInvoiceErrors(nextInvoiceErrors)
          if (hasInvoiceError(nextInvoiceErrors)) {
            setSubmitError(t('billing.invoiceFixBeforePay'))
            return
          }
          try {
            await invoiceSaveFn(invoiceSettingsFromForm(form))
          } catch (cause) {
            reportHandledException(cause, {
              source: 'features/billing/checkout/checkout-panel.tsx',
            })
            console.warn('checkout: failed to save invoice settings — charging anyway', cause)
          }
        }
        const outcome = await strategy.submit({
          prime: payload.prime,
          cardholder: payload.cardholder,
          couponCode: appliedCoupon?.code,
          checkoutAttemptId,
        })
        switch (outcome.kind) {
          case 'noop':
            break
          case 'success':
            onClose()
            onSuccessOutcome?.(outcome)
            onSuccess?.(outcome.tradeId)
            break
          case 'redirect':
            navigate(outcome.url)
            break
          case 'requiresSignIn':
            onClose()
            onRequiresSignIn?.(outcome.email, outcome.paid === true)
            break
          case 'cardRequired':
            // Wallet no longer covers the price → drop the card-free button and
            // show the card form for the remaining cash.
            setForceCard(true)
            setSubmitError(t('billing.walletNoLongerCovers'))
            onCardRequired?.()
            break
          case 'couponInvalid':
            // The server refused the coupon at submit → drop it so the next
            // attempt charges the correct (undiscounted) price, and say why when
            // the server told us (a bound code is not "lapsed").
            setAppliedCoupon(null)
            setSubmitError(
              couponReasonMessage(t, outcome.reason, 'billing.couponInvalidAtCheckout', {
                signedIn: readStoredToken() !== null,
              })
            )
            break
          case 'recoveryPending':
            setRecoveryPending(true)
            setSubmitError(outcome.message)
            break
          case 'declined':
            setSubmitError(outcome.message)
            break
          case 'error':
            setSubmitError(outcome.message)
            setSubmitErrorAction(outcome.action ?? null)
            break
        }
      } finally {
        setSubmitting(false)
      }
    },
    [
      strategy,
      appliedCoupon,
      checkoutAttemptId,
      navigate,
      onClose,
      onSuccess,
      onSuccessOutcome,
      onRequiresSignIn,
      onCardRequired,
      showInvoice,
      invoiceSaveFn,
      recoveryPending,
      t,
    ]
  )

  const submitLabel =
    submitLabelOverride ?? t('billing.modalSubmit', { amount: formatAmount(payable) })

  // Trust/security note — boxed, with a lock glyph (checkout-redesign.html).
  const noteNode = (
    <p className="checkout-note">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        aria-hidden="true"
      >
        <rect x="4" y="10" width="16" height="11" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      </svg>
      <span>{note}</span>
    </p>
  )

  // Order summary: 折抵券 + the 金額明細 breakdown box. Always shows 小計 + 應付;
  // the 折抵券折抵 / 折抵儲值金 rows appear only when an offset applies (and carry
  // `variant: 'discount'` → the saved amount renders green). Null for bind-card.
  const breakdownRows: ReceiptRow[] = [
    {
      key: 'subtotal',
      label: subtotalLabel ?? t('billing.modalSubtotalCaption'),
      value: t('billing.modalAmountLine', { amount: formatAmount(amount) }),
    },
    ...(couponApplied > 0
      ? [
          {
            key: 'coupon',
            label: t('billing.modalCouponOffsetCaption'),
            value: t('billing.modalWalletOffsetLine', { amount: formatAmount(couponApplied) }),
            variant: 'discount' as const,
            'data-testid': `${testIdPrefix}-coupon-offset`,
          },
        ]
      : []),
    ...(walletApplied > 0
      ? [
          {
            key: 'wallet',
            label: t('billing.modalWalletOffsetCaption'),
            value: t('billing.modalWalletOffsetLine', { amount: formatAmount(walletApplied) }),
            variant: 'discount' as const,
            'data-testid': `${testIdPrefix}-wallet-offset`,
          },
        ]
      : []),
    {
      key: 'payable',
      label: t('billing.modalPayableCaption'),
      value: t('billing.modalAmountLine', { amount: formatAmount(payable) }),
      variant: 'total',
      'data-testid': `${testIdPrefix}-payable`,
    },
  ]

  const summaryNode = hasSummary ? (
    <>
      {/* Coupon field only for purchases — a wallet top-up shows the summary
          (金額明細 + 發票) but takes no coupon. */}
      {coupon !== null && (
        <>
          <p className="checkout-col-title">{t('billing.couponLabel')}</p>
          <CouponField
            product={coupon.product}
            planCode={coupon.planCode}
            cycle={coupon.cycle}
            packCode={coupon.packCode}
            seats={coupon.seats}
            applied={appliedCoupon}
            onApplied={setAppliedCoupon}
            onCleared={() => setAppliedCoupon(null)}
            disabled={controlsDisabled}
            showLabel={false}
          />
        </>
      )}
      <p className="checkout-col-title">{t('billing.modalSummaryTitle')}</p>
      <ReceiptRows data-testid={`${testIdPrefix}-amount-breakdown`} rows={breakdownRows} />
      {summaryNote}
      {showInvoice ? (
        <>
          <p className="checkout-col-title">{t('billing.invoiceHeading')}</p>
          <InvoiceFields
            value={invoiceForm}
            errors={invoiceErrors}
            email={userEmail}
            disabled={controlsDisabled}
            onChange={(next) => {
              invoiceUserEditedRef.current = true
              applyInvoiceForm(next)
              setInvoiceErrors(NO_INVOICE_ERRORS)
            }}
          />
        </>
      ) : null}
    </>
  ) : null

  const errorNode = submitError ? (
    <p className="alert show" role="alert" data-testid={`${testIdPrefix}-checkout-error`}>
      {submitError}
      {submitErrorAction ? (
        <>
          {' '}
          <a className="link" href={submitErrorAction.href}>
            {submitErrorAction.label}
          </a>
        </>
      ) : null}
    </p>
  ) : null

  const cardFormNode = skipScript ? (
    <KiaoCardForm
      ready
      disabled={controlsDisabled}
      initialCardholder={userEmail ? { email: userEmail } : undefined}
      onSubmit={runSubmit}
      submitLabel={submitLabel}
      submittingLabel={t('billing.modalSubmitting')}
      emailHint={emailHint}
    />
  ) : (
    <TappayScript>
      {(ready) => (
        <KiaoCardForm
          ready={ready}
          disabled={controlsDisabled}
          initialCardholder={userEmail ? { email: userEmail } : undefined}
          onSubmit={runSubmit}
          submitLabel={submitLabel}
          submittingLabel={t('billing.modalSubmitting')}
          emailHint={emailHint}
        />
      )}
    </TappayScript>
  )

  // Payment field — the card form (with its 持卡資訊 section title in the 2-col
  // purchase layout), or a single card-free CTA when cash = NT$0.
  const paymentNode = (
    <div data-testid={formTestId}>
      {fullyCovered && cardFree ? (
        // Wallet and/or coupon covers the whole price → no card. Submit with no
        // prime; the server mints an NT$0 internal trade. The label is neutral
        // when no wallet was used (coupon-only coverage).
        <Button
          type="button"
          className="w-full justify-center"
          data-testid={`${testIdPrefix}-wallet-pay`}
          disabled={controlsDisabled}
          onClick={() =>
            runSubmit({
              cardholder: { name: userEmail ?? 'wallet', email: userEmail ?? '' },
            })
          }
        >
          {submitting
            ? t('billing.modalSubmitting')
            : walletApplied > 0
              ? t('billing.modalWalletPay')
              : t('billing.modalZeroPay')}
        </Button>
      ) : (
        <>
          {hasSummary ? (
            <p className="checkout-col-title">{t('billing.modalCardSectionTitle')}</p>
          ) : null}
          {cardFormNode}
        </>
      )}
    </div>
  )

  // Purchase flows get the two-column order-summary + payment layout (collapses
  // to one column on mobile); bind-card has no summary so it's a single column
  // (.checkout-cols--single). The `.checkout-cols` wrapper and the payment column
  // are rendered in the SAME position in both modes — only the summary column
  // (index 0) toggles — so the payment column (index 1, holding the TapPay card
  // form) never remounts when a billing dialog switches purchase ↔ bind, keeping
  // the hosted iframes alive (their setup() runs once per mount).
  return (
    <div className="km-body">
      {setup ? (
        <div
          className="checkout-setup"
          inert={controlsDisabled ? true : undefined}
          aria-disabled={controlsDisabled || undefined}
        >
          {setup}
        </div>
      ) : null}
      <div className={`checkout-cols${hasSummary ? '' : ' checkout-cols--single'}`}>
        {hasSummary ? (
          <div className="checkout-col checkout-col--summary">
            {noteNode}
            {summaryNode}
          </div>
        ) : null}
        <div className="checkout-col checkout-col--pay">
          {hasSummary ? null : noteNode}
          {errorNode}
          {paymentNode}
        </div>
      </div>
    </div>
  )
}
